import { FC, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../context/notificationContext";
import { useUserSettings } from "../../../context/userSettingsContext";
import useDataInitializer from "../../../hooks/useDataInitializer";
import { useDebounce } from "../../../hooks/useDebounce";
import useListFilters from "../../../hooks/useListFilters";
import { useI18n } from "../../../hooks/useTranslations";
import { getCategories } from "../../../http/cars/types";
import { useCostImportsApi } from "../../../http/costImports/api";
import { useDkvTollImportsApi as useDkvTollsImportsApi } from "../../../http/highwayTollCostImports/api";
import { DkvTollsImport } from "../../../http/highwayTollCostImports/types";
import { PaginationQuery } from "../../../types";
import { downloadFileFromData, formatCurrency, formatDateNumeric } from "../../../types/utils";
import CostTypes from "../../costs/types/CostTypes";
import SvgInfo from "../../icons/Info";
import { ErrorBox } from "../../ui/Boxes";
import Button from "../../ui/Button";
import CustomLink from "../../ui/CustomLink";
import ExportButton from "../../ui/ExportButton";
import Icon from "../../ui/Icon";
import Table from "../../ui/Table";
import Tooltip from "../../ui/Tooltip";
import TextColumnFilter from "../../ui/columnFilters/TextColumnFilter";
import CarAssignmentCell from "../CarAssignmentCell";
import CarAssignmentDialog from "../CarAssignmentDialog";
import styles from "./DkvTollsCostImport.module.css";
import ImportDkvTollsCostImportButton from "./ImportDkvTollsCostImportButton";

interface DkvTollImportProps {
  withErrors?: boolean;
  notExported?: boolean;
  forced?: boolean;
}

const DkvTollsImportLines: FC<DkvTollImportProps> = ({ withErrors, notExported, forced }: DkvTollImportProps) => {
  const i18n = useI18n();
  const dkvTollsImportApi = useDkvTollsImportsApi();
  const userSettings = useUserSettings();
  const filters = useListFilters({
    isSearchable: true,
    prefix:
      withErrors === true
        ? "toll_err"
        : withErrors === false
          ? "toll_no_err"
          : notExported
            ? "toll_not_exp"
            : forced
              ? "toll_forced"
              : undefined,
  });
  const costImportsApi = useCostImportsApi();
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const dispatch = useContext(NotificationDispatch);
  const { id } = useParams<{ id: string }>();
  const [showCostTypes, setShowCostTypes] = useState(false);
  const [assignmentItem, setAssignmentItem] = useState<DkvTollsImport | null>(null);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { data: costImport, trigger } = costImportsApi.getCostImport(id!, aborter.current.signal);

  const result = dkvTollsImportApi.useDkvTollsImport(
    query,
    aborter.current.signal,
    id!,
    withErrors,
    notExported,
    forced,
  );
  const [data] = useDataInitializer(result.data, result.error);

  const dataWithErrors = dkvTollsImportApi.useDkvTollsImport({}, aborter.current.signal, id!, true);

  const subsidiary = costImport?.subsidiary;
  const getSubsidiaryEditUrl = (subsidiaryId: string, tab?: number) => {
    const params = new URLSearchParams();
    tab !== undefined && params.set("tab", String(tab));
    params.set("redirect_uri", window.location.pathname + window.location.search);
    return `/admin/subsidiaries/${subsidiaryId}?${params.toString()}`;
  };
  const missingSubsidiaryExportSettings = !subsidiary
    ? []
    : [
        !subsidiary.vatServiceCode && i18n.t.subsidiaries.errors.missingVatServiceCode,
        !subsidiary.countryIsoCodeAlpha2 && i18n.t.subsidiaries.errors.missingCountryIsoCode,
        !subsidiary.vatDeductionCode && i18n.t.subsidiaries.errors.missingVatDeductionCode,
      ].filter((message): message is string => !!message);

  useEffect(() => {
    (result.error || dataWithErrors.error) &&
      dispatch(showErrorNotification(i18n, result.error || dataWithErrors.error));
  }, [result.error, dataWithErrors.error]);

  useEffect(() => {
    !!id && trigger();
  }, [id]);

  const filterOptions = {
    get: dkvTollsImportApi.getDkvTollssFieldFilters,
    update: filters.setFilters,
    getSelected: filters.getFilters,
  };

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const reloadData = async () => {
    try {
      await dkvTollsImportApi.reloadDkvTollsLines(id!);
      dispatch(showSuccessNotification(i18n.t));
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    }
  };

  const categories = getCategories(i18n.t);

  const getRowMeta = (item: DkvTollsImport) => {
    const formattedDate = formatDateNumeric(item.date, i18n.language);
    const assignedUser = item.assignment?.carDriver?.driver;
    const assignment = assignedUser?.fullName || item.assignment?.otherCarAssignment?.type || null;
    const missingCostCenterMessage = assignment
      ? i18n.t.costsImport.dkv.noCostCenterFound(assignment, formattedDate)
      : i18n.t.costsImport.dkv.noUserCostCenterFound(item.carPlateNr, formattedDate);

    // only pkw, pkw-superior, lkw are exported
    const isToExport = !item.car || (!!item.car?.category && item.car.category !== "pkw-up");
    const hasMissingDriver = !assignedUser && !assignment;

    return { formattedDate, assignedUser, assignment, missingCostCenterMessage, isToExport, hasMissingDriver };
  };

  const renderAssignmentDialog = (item: DkvTollsImport) => {
    const { formattedDate, assignment } = getRowMeta(item);

    return (
      <CarAssignmentDialog
        date={item.date}
        formattedDate={formattedDate}
        originalPlate={item.carPlateNr}
        carId={item.car?.id}
        plateHint={item.car ? undefined : item.carPlateNr}
        forcedByUser={item.carForcedByUser}
        forcedAt={item.carForcedAt}
        assignmentForcedByUser={item.assignmentForcedByUser}
        assignmentForcedAt={item.assignmentForcedAt}
        forcedAssignmentId={item.assignment?.carDriver?.id ?? item.assignment?.otherCarAssignment?.id}
        forcedAssignmentName={assignment ?? undefined}
        onSave={async (car) => {
          try {
            await dkvTollsImportApi.forceCar(item.id, car.id, id!);
            dispatch(showSuccessNotification(i18n.t));
          } catch (error) {
            dispatch(showErrorNotification(i18n, error));
          }
        }}
        onRemoveForce={async () => {
          try {
            await dkvTollsImportApi.removeForcedCar(item.id, id!);
            dispatch(showSuccessNotification(i18n.t));
          } catch (error) {
            dispatch(showErrorNotification(i18n, error));
          }
        }}
        onForceAssignment={async (carId, assignmentId, assignmentType) => {
          try {
            await dkvTollsImportApi.forceAssignment(item.id, carId, assignmentId, assignmentType, id!);
            dispatch(showSuccessNotification(i18n.t));
            return true;
          } catch (error) {
            dispatch(showErrorNotification(i18n, error));
            return false;
          }
        }}
        onAssignDriver={async (carId) => {
          try {
            await dkvTollsImportApi.removeForcedAssignment(item.id, carId, id!);
            dispatch(showSuccessNotification(i18n.t));
            return true;
          } catch (error) {
            dispatch(showErrorNotification(i18n, error));
            return false;
          }
        }}
        onClose={() => setAssignmentItem(null)}
      />
    );
  };

  return (
    <>
      {missingSubsidiaryExportSettings.length > 0 && (
        <ErrorBox
          text={
            <>
              {missingSubsidiaryExportSettings.join(" / ")}
              {!!subsidiary && <CustomLink to={getSubsidiaryEditUrl(subsidiary.id)}>{i18n.t.common.edit}</CustomLink>}
            </>
          }
          className={styles.subsidiaryExportWarning}
        />
      )}
      <Table<DkvTollsImport>
        filters={filters}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        buttons={{
          secondary: userSettings.permissions.costsImports.insert
            ? [
                <Button
                  buttonProps={{
                    onClick: () => reloadData(),
                    disabled: !!costImport?.isImportedInCosts,
                  }}
                >
                  {i18n.t.common.reloadData}
                </Button>,
                userSettings.permissions.costTypes.read && (
                  <Button
                    buttonProps={{
                      onClick: () => setShowCostTypes(true),
                    }}
                  >
                    {i18n.t.common.manageCostTypes}
                  </Button>
                ),
                !!dataWithErrors && dataWithErrors.data?.total == 0 && (
                  <ImportDkvTollsCostImportButton
                    isDisabled={!costImport || costImport?.isImportedInCosts}
                    onUpdate={trigger}
                  />
                ),
                !!dataWithErrors &&
                  dataWithErrors.data?.total == 0 &&
                  (() => {
                    const exportButton = (
                      <ExportButton.Excel
                        disabled={missingSubsidiaryExportSettings.length > 0}
                        onClick={async () => {
                          try {
                            const r = await dkvTollsImportApi.exportExcel(id!, aborter.current.signal);
                            downloadFileFromData(r.data, "export", r.contentType);
                          } catch (error) {
                            dispatch(showErrorNotification(i18n, error));
                          }
                        }}
                      />
                    );

                    return missingSubsidiaryExportSettings.length > 0 ? (
                      <Tooltip
                        message={missingSubsidiaryExportSettings.join(" / ")}
                        to={subsidiary ? getSubsidiaryEditUrl(subsidiary.id) : undefined}
                        isError
                      >
                        {exportButton}
                      </Tooltip>
                    ) : (
                      exportButton
                    );
                  })(),
              ]
            : undefined,
        }}
        head={
          <>
            <Table.Th
              fieldName="toll.carPlate.number"
              title={i18n.t.plateNumbers.singular}
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            <Table.Th
              fieldName="toll.car"
              title={i18n.t.cars.singular}
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            {forced && <Table.Th fieldName="toll.carForcedBy" title={i18n.t.common.forcedBy} />}
            <Table.Th
              fieldName="toll.car.category"
              title={i18n.t.common.category}
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            <Table.Th fieldName="toll.date" title={i18n.t.common.date} order={orderOptions} />
            <Table.Th fieldName="toll.assignment" title={i18n.t.drivers.singular} />
            <Table.Th
              fieldName="toll.costCenter"
              title={i18n.t.common.costCenter}
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            <Table.Th fieldName="toll.vat" title={i18n.t.common.vat} order={orderOptions} />
            <Table.Th fieldName="toll.cost" title={i18n.t.common.cost} order={orderOptions} />
            <Table.Th fieldName="toll.currency" title={i18n.t.common.currency} order={orderOptions} />
            <Table.Th
              fieldName="toll.productCode"
              title={i18n.t.common.productCode}
              order={orderOptions}
              filters={filterOptions}
              filter={TextColumnFilter}
            />
            <Table.Th
              fieldName="toll.serviceCountryCode"
              title={i18n.t.common.country}
              order={orderOptions}
              filters={filterOptions}
              filter={TextColumnFilter}
            />
          </>
        }
        renderRow={(item) => {
          const { formattedDate, assignedUser, assignment, missingCostCenterMessage, isToExport, hasMissingDriver } =
            getRowMeta(item);
          const subsidiaryId = costImport?.subsidiary?.id;

          const excludedMessage = i18n.t.costsImport.excludedFromExport(item.carPlateNr);
          const forcedMessage = item.carForcedAt
            ? i18n.t.costsImport.carForcedManually(formatDateNumeric(item.carForcedAt, i18n.language))
            : item.assignmentForcedAt
              ? i18n.t.costsImport.assignmentForcedManually(formatDateNumeric(item.assignmentForcedAt, i18n.language))
              : undefined;
          const wrapPlain = (content: React.ReactNode) =>
            !isToExport && !notExported ? (
              <Tooltip message={excludedMessage} children={content} />
            ) : forcedMessage ? (
              <Tooltip message={forcedMessage} children={content} />
            ) : (
              content
            );

          const carCellContent = item.car ? (
            forcedMessage ? (
              <Tooltip
                message={forcedMessage}
                children={
                  <CustomLink to={`/admin/cars/${item.car.id}`}>
                    {item.car.currentPlateNumber || item.car.lastPlateNumber}
                  </CustomLink>
                }
              />
            ) : (
              <CustomLink to={`/admin/cars/${item.car.id}`}>
                {item.car.currentPlateNumber || item.car.lastPlateNumber}
              </CustomLink>
            )
          ) : (
            <Tooltip
              message={i18n.t.costsImport.dkv.noCarFound(item.carPlateNr, formattedDate)}
              children={item.carPlateNr}
              isError
            />
          );

          return (
            <>
              <td>{wrapPlain(item.carPlateNr)}</td>
              <td>
                {isToExport ? (
                  <CarAssignmentCell
                    canEdit={
                      hasMissingDriver ||
                      (!!item.carForcedByUser && !!item.carForcedAt) ||
                      (!!item.assignmentForcedByUser && !!item.assignmentForcedAt)
                    }
                    onEdit={() => setAssignmentItem(item)}
                  >
                    {carCellContent}
                  </CarAssignmentCell>
                ) : (
                  wrapPlain(item.carPlateNr)
                )}
              </td>
              {forced && <td>{item.carForcedByUser?.fullName ?? item.assignmentForcedByUser?.fullName}</td>}
              <td>{wrapPlain(categories.find((x) => x.value == item.car?.category)?.label)}</td>
              <td>{wrapPlain(formattedDate)}</td>
              <td>
                {isToExport ? (
                  !item.costCenter ? (
                    assignedUser ? (
                      <Tooltip
                        message={missingCostCenterMessage}
                        to={`/admin/users/${assignedUser.id}`}
                        children={assignedUser.fullName}
                        isError
                      />
                    ) : assignment ? (
                      <Tooltip message={missingCostCenterMessage} children={assignment} isError />
                    ) : (
                      <Tooltip message={i18n.t.costsImport.dkv.noDriverFound(item.carPlateNr, formattedDate)} isError />
                    )
                  ) : assignedUser ? (
                    wrapPlain(<CustomLink to={`/admin/users/${assignedUser.id}`}>{assignedUser.fullName}</CustomLink>)
                  ) : assignment ? (
                    wrapPlain(assignment)
                  ) : (
                    <Tooltip message={i18n.t.costsImport.dkv.noDriverFound(item.carPlateNr, formattedDate)} isError />
                  )
                ) : (
                  wrapPlain(assignedUser?.fullName || assignment || null)
                )}
              </td>
              <td>
                {isToExport ? (
                  !item.costCenter ? (
                    <Tooltip message={missingCostCenterMessage} isError />
                  ) : (
                    wrapPlain(item.costCenter)
                  )
                ) : (
                  wrapPlain(item.costCenter)
                )}
              </td>
              <td>{wrapPlain(formatCurrency(item.vat, i18n.language))}</td>
              <td>{wrapPlain(formatCurrency(item.costGross, i18n.language))}</td>
              <td>{wrapPlain(item.currency)}</td>
              <td>
                {isToExport ? (
                  !item.subsidiaryVatCodeId ? (
                    <Tooltip
                      message={i18n.t.costsImport.dkv.noSubsidiaryVatCodeFound(item.productCode)}
                      to={subsidiaryId ? getSubsidiaryEditUrl(subsidiaryId, 1) : undefined}
                      children={
                        <span className={styles.inlineInfo}>
                          {item.productCode}
                          <Icon glyph={SvgInfo} className={styles.inlineInfoIcon} />
                        </span>
                      }
                      isError
                    />
                  ) : !item.costTypeId ? (
                    <Tooltip
                      message={i18n.t.costsImport.dkv.noCostTypeFound(item.productCode)}
                      children={
                        <span className={styles.inlineInfo}>
                          {item.productCode}
                          <Icon glyph={SvgInfo} className={styles.inlineInfoIcon} />
                        </span>
                      }
                      isError
                    />
                  ) : (
                    wrapPlain(item.productCode)
                  )
                ) : (
                  wrapPlain(item.productCode)
                )}
              </td>
              <td>{wrapPlain(item.serviceCountryCode)}</td>
            </>
          );
        }}
        getRowStyle={(item) => {
          if (!item.car) {
            return { "--row-bg": "var(--red-20)", "--row-hover-bg": "var(--red-40)" } as React.CSSProperties;
          }
          if (item.carForcedAt || item.assignmentForcedAt) {
            return { "--row-bg": "var(--blue-20)", "--row-hover-bg": "var(--blue-40)" } as React.CSSProperties;
          }
          if (!item.car?.category || item.car?.category === "pkw-up") {
            return { "--row-bg": "var(--yellow-20)", "--row-hover-bg": "var(--yellow-40)" } as React.CSSProperties;
          }
          return undefined;
        }}
        error={result.error}
        items={data?.items}
        total={data?.total}
      />

      {assignmentItem &&
        renderAssignmentDialog(data?.items.find((item) => item.id === assignmentItem.id) ?? assignmentItem)}

      {showCostTypes && <CostTypes onClose={() => setShowCostTypes(false)} />}
    </>
  );
};

export default DkvTollsImportLines;
